/**
 * pasteCleaner.js — defensive paste-cleanup utility.
 *
 * Why this exists: users sometimes paste text into our app (or into their EMR)
 * that has been URL-encoded (%20 for space, %0A for newline, %E2%80%A2 for •).
 * This typically happens when text is copied from a browser URL bar, a deep
 * link, or an EMR that stores notes as URI-encoded strings. Pasting that
 * encoded blob raw is unreadable for the user.
 *
 * `cleanPastedText()` heuristically detects URL-encoded content and decodes
 * it. If the input doesn't look URL-encoded, it's returned untouched.
 *
 * Usage: attach to <textarea onPaste={smartPaste(setValue)}> or call
 * cleanPastedText(value) before storing.
 */

/**
 * Detect whether a string looks URL-encoded enough to warrant decoding.
 * Heuristics:
 *  - Contains percent-encoded byte sequences (e.g. %20, %0A, %E2%80%A2)
 *  - The encoded sequences are dense (>= 3% of the string is %XX)
 *  - At least one whitespace placeholder (%20) OR newline (%0A)
 */
export const looksUrlEncoded = (raw) => {
  if (raw == null) return false;
  const s = String(raw);
  if (s.length < 6) return false;
  const matches = s.match(/%[0-9A-Fa-f]{2}/g);
  if (!matches || matches.length < 3) return false;
  const density = matches.length / s.length;
  if (density < 0.02) return false;
  // Require a space or newline marker so we don't accidentally decode a real URL
  return /%20|%0A|%E2%80%A2/.test(s);
};

/**
 * Decode URL-encoded text if needed. Safe for plain text — returns unchanged
 * input when nothing looks encoded.
 */
export const cleanPastedText = (raw) => {
  if (raw == null) return '';
  const s = String(raw);
  if (!looksUrlEncoded(s)) return s;
  try {
    // decodeURIComponent throws on malformed sequences (e.g. lone %); fall
    // back to a forgiving regex decode that simply leaves bad escapes alone.
    return decodeURIComponent(s);
  } catch {
    try {
      return s.replace(/%[0-9A-Fa-f]{2}/g, (m) => {
        try {
          return decodeURIComponent(m);
        } catch {
          return m;
        }
      });
    } catch {
      return s;
    }
  }
};

/**
 * onPaste handler factory. Use as:
 *   <textarea onPaste={smartPaste(setValue, value)} ... />
 *
 * Reads the clipboard content, decodes if URL-encoded, then performs the
 * insertion manually so the text shows up correctly without the browser's
 * default raw paste.
 */
export const smartPaste = (setValue, currentValue = '') => (e) => {
  const raw = e.clipboardData?.getData('text/plain') ?? '';
  if (!looksUrlEncoded(raw)) {
    // Let the default paste happen so cursor/selection behave naturally.
    return;
  }
  e.preventDefault();
  const cleaned = cleanPastedText(raw);
  const target = e.target;
  const start = typeof target.selectionStart === 'number' ? target.selectionStart : currentValue.length;
  const end = typeof target.selectionEnd === 'number' ? target.selectionEnd : currentValue.length;
  const next = (currentValue || '').slice(0, start) + cleaned + (currentValue || '').slice(end);
  setValue(next);
  // Restore cursor position after the pasted block
  requestAnimationFrame(() => {
    try {
      target.selectionStart = target.selectionEnd = start + cleaned.length;
    } catch {
      /* element may have unmounted */
    }
  });
};

export default cleanPastedText;
